import { emptyRulesForm, rulesFromSettings, type RulesFormState } from '@/components/event-wizard/wizard-utils';

export type AttendanceSettingsState = {
  enabled: boolean;
  multiple_sessions_per_day: boolean;
  allow_late_scans: boolean;
  manual_attendance_allowed: boolean;
};

export type EvaluationSettingsState = {
  enabled: boolean;
  scoring_mode: 'average' | 'weighted' | 'custom';
  score_scale: number;
  jury_visibility: 'public' | 'organizers_only';
  result_publish_mode: 'manual' | 'automatic';
};

export type NotificationSettingsState = {
  email_updates: boolean;
  whatsapp_updates: boolean;
  campaign_reminders: boolean;
  announcement_notifications: boolean;
};

export type DiscoverySettingsState = {
  seo_title: string;
  seo_description: string;
  discoverability: 'featured' | 'searchable' | 'hidden';
};

export type BrandingSettingsState = {
  accent_color: string;
  cover_override: string;
  banner_override: string;
};

export type AdvancedSettingsFormState = {
  attendance: AttendanceSettingsState;
  evaluation: EvaluationSettingsState;
  notifications: NotificationSettingsState;
  discovery: DiscoverySettingsState;
  branding: BrandingSettingsState;
  rules: RulesFormState;
};

export function emptyAdvancedSettingsForm(): AdvancedSettingsFormState {
  return {
    attendance: {
      enabled: false,
      multiple_sessions_per_day: false,
      allow_late_scans: false,
      manual_attendance_allowed: false,
    },
    evaluation: {
      enabled: false,
      scoring_mode: 'average',
      score_scale: 10,
      jury_visibility: 'organizers_only',
      result_publish_mode: 'manual',
    },
    notifications: {
      email_updates: true,
      whatsapp_updates: false,
      campaign_reminders: true,
      announcement_notifications: true,
    },
    discovery: {
      seo_title: '',
      seo_description: '',
      discoverability: 'searchable',
    },
    branding: {
      accent_color: '',
      cover_override: '',
      banner_override: '',
    },
    rules: emptyRulesForm(),
  };
}

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function advancedSettingsFromEvent(
  event: Record<string, unknown> | null | undefined,
): AdvancedSettingsFormState {
  const base = emptyAdvancedSettingsForm();
  if (!event) return base;

  const settings = (event.settings ?? {}) as Record<string, unknown>;
  const attendance = (settings.attendance ?? {}) as Record<string, unknown>;
  const evaluation = (settings.evaluation ?? {}) as Record<string, unknown>;
  const notifications = (settings.notifications ?? {}) as Record<string, unknown>;
  const discovery = (settings.discovery ?? {}) as Record<string, unknown>;
  const branding = (settings.branding ?? {}) as Record<string, unknown>;

  return {
    attendance: {
      enabled: asBool(attendance.enabled),
      multiple_sessions_per_day: asBool(attendance.multiple_sessions_per_day),
      allow_late_scans: asBool(attendance.allow_late_scans),
      manual_attendance_allowed: asBool(attendance.manual_attendance_allowed),
    },
    evaluation: {
      enabled: evaluation.enabled !== false,
      scoring_mode:
        evaluation.scoring_mode === 'weighted' || evaluation.scoring_mode === 'custom'
          ? evaluation.scoring_mode
          : 'average',
      score_scale: typeof evaluation.score_scale === 'number' ? evaluation.score_scale : 10,
      jury_visibility: evaluation.jury_visibility === 'public' ? 'public' : 'organizers_only',
      result_publish_mode: evaluation.result_publish_mode === 'automatic' ? 'automatic' : 'manual',
    },
    notifications: {
      email_updates: notifications.email_updates !== false,
      whatsapp_updates: asBool(notifications.whatsapp_updates),
      campaign_reminders: notifications.campaign_reminders !== false,
      announcement_notifications: notifications.announcement_notifications !== false,
    },
    discovery: {
      seo_title: typeof discovery.seo_title === 'string' ? discovery.seo_title : '',
      seo_description: typeof discovery.seo_description === 'string' ? discovery.seo_description : '',
      discoverability:
        discovery.discoverability === 'featured' || discovery.discoverability === 'hidden'
          ? discovery.discoverability
          : 'searchable',
    },
    branding: {
      accent_color: typeof branding.accent_color === 'string' ? branding.accent_color : '',
      cover_override:
        typeof branding.cover_override === 'string'
          ? branding.cover_override
          : typeof event.cover_image === 'string'
            ? event.cover_image
            : '',
      banner_override:
        typeof branding.banner_override === 'string'
          ? branding.banner_override
          : typeof event.banner === 'string'
            ? event.banner
            : '',
    },
    rules: rulesFromSettings(settings),
  };
}

export function buildAdvancedSettingsPayload(form: AdvancedSettingsFormState): Record<string, unknown> {
  // Evaluation + discovery are managed in the event workspace — do not overwrite from the wizard.
  return {
    attendance: { ...form.attendance },
    notifications: {
      ...form.notifications,
      // WhatsApp updates are hidden in the wizard; always persist false for new saves.
      whatsapp_updates: false,
    },
    branding: { ...form.branding },
    rules_text: form.rules.rules_text.trim(),
    eligibility_criteria: form.rules.eligibility_criteria.trim(),
    code_of_conduct: form.rules.code_of_conduct.trim(),
    disqualification_rules: form.rules.disqualification_rules.trim(),
  };
}
